import { EventSignUpRegistration } from "../types";

const getNumRequiredMemberSlots = (
  minMemberCount: number,
  numMemberRegistrations: number
) => {
  return Math.max(0, minMemberCount - numMemberRegistrations);
};

// Returns the number of required guest slots.
function getNumRequiredGuestSlots(
  minGuestCount: number,
  numGuestRegistrations: number
) {
  return Math.max(0, minGuestCount - numGuestRegistrations);
}

function getNumRequiredCustomerSlots(
  minCustomerCount: number,
  numCustomerRegistrations: number
) {
  return Math.max(0, minCustomerCount - numCustomerRegistrations);
}

// This function returns the number of guest registrations for the given event.
const getNumGuestRegistrations = (
  registrations?: EventSignUpRegistration[]
) => {
  return (
    registrations?.filter(
      (registration) =>
        registration.type === "GUEST" || registration.type === "New Guest"
    ).length ?? 0
  );
};

const getNumMemberRegistrations = (
  registrations?: EventSignUpRegistration[]
) => {
  return (
    registrations?.filter((registration) => registration.type === "MEMBER")
      .length ?? 0
  );
};

const getNumMemberHoldRegistrations = (
  registrations?: EventSignUpRegistration[]
) => {
  return (
    registrations?.filter(
      (registration) =>
        registration.type === "MEMBER" && !registration?.registrant?.playerId
    ).length ?? 0
  );
};

const getNumCustomerHoldRegistrations = (
  registrations?: EventSignUpRegistration[]
) => {
  return (
    registrations?.filter(
      (registration) =>
        registration.type === "CUSTOMER" && !registration?.registrant?.playerId
    ).length ?? 0
  );
};

const getNumCustomerRegistrations = (
  registrations?: EventSignUpRegistration[]
) => {
  return (
    registrations?.filter((registration) => registration.type === "CUSTOMER")
      .length ?? 0
  );
};

export const getRequiredAndEmptySlots = (
  allowedGroupSizes: readonly (number | null)[],
  maxGuestCount: number,
  maxMemberCount: number,
  minGuestCount: number,
  minMemberCount: number,
  allRegistrations: EventSignUpRegistration[],
  remainingCapacityInEvent: number,
  originalRegistrationsCount: number,
  supportsPublic: boolean
) => {
  /* we need to cut higher number of members/guests, because of allowed group */
  const allowedGroupSizesFiltered = allowedGroupSizes.map((size) => size || 0);

  const maxCalculatedGroupSize = Math.max(
    ...(allowedGroupSizesFiltered.map((a) => a ?? 0) ?? 0)
  );
  /* now we need to figure out if the originalRegistrationsCount + remainingCapacityInEvent
  /* this sets the maximun size of the group to the remain capacity of the event if relevant */
  const alternateCapacity =
    originalRegistrationsCount + remainingCapacityInEvent;
  const maxGroupSize = Math.min(maxCalculatedGroupSize, alternateCapacity);
  const minGroupSize = Math.min(
    ...(allowedGroupSizesFiltered.map((a) => a ?? 0) ?? 0)
  );

  const numMemberRegistrations = getNumMemberRegistrations(allRegistrations);
  const numGuestRegistrations = getNumGuestRegistrations(allRegistrations);

  const numCustomerRegistrations =
    getNumCustomerRegistrations(allRegistrations);

  const numCustomerHoldRegistrations =
    getNumCustomerHoldRegistrations(allRegistrations);
  const numMemberHoldRegistrations =
    getNumMemberHoldRegistrations(allRegistrations);

  const numGuestAndMemberRegistrations =
    numMemberRegistrations + numGuestRegistrations;

  /* We calculate required slots here */
  const numRequiredMemberSlots = supportsPublic
    ? 0 // When supportsPublic is true, members don't have requirements (customers do)
    : getNumRequiredMemberSlots(
        minMemberCount,
        numMemberRegistrations - numMemberHoldRegistrations
      );

  const numRequiredGuestSlots = getNumRequiredGuestSlots(
    minGuestCount,
    numGuestRegistrations
  );

  // When supportsPublic is true, customers are treated like members (using minMemberCount)
  // When supportsPublic is false, members use minMemberCount as usual
  const numRequiredCustomerSlots = supportsPublic
    ? getNumRequiredCustomerSlots(
        minMemberCount,
        numCustomerRegistrations - numCustomerHoldRegistrations
      )
    : 0;

  const numRequiredFlexSlots = supportsPublic
    ? Math.max(
        minGroupSize - numCustomerRegistrations - numRequiredCustomerSlots,
        0
      )
    : Math.max(
        minGroupSize -
          numGuestAndMemberRegistrations -
          numRequiredMemberSlots -
          numRequiredGuestSlots,
        0
      );

  const numOptionalSlots = supportsPublic
    ? Math.max(
        maxGroupSize -
          numRequiredCustomerSlots -
          numRequiredFlexSlots -
          numCustomerRegistrations,
        0
      )
    : Math.max(
        maxGroupSize -
          numRequiredMemberSlots -
          numRequiredGuestSlots -
          numRequiredFlexSlots -
          numGuestAndMemberRegistrations,
        0
      );

  const numOptionalMemberSlots = Math.max(
    numOptionalSlots -
      maxGuestCount +
      Math.max(numRequiredGuestSlots, numGuestRegistrations),
    0
  );

  const numOptionalGuestSlots = Math.max(
    numOptionalSlots -
      maxMemberCount +
      Math.max(numRequiredMemberSlots, numMemberRegistrations),
    0
  );

  const numOptionalCustomerSlots = supportsPublic
    ? Math.max(
        maxGroupSize - numRequiredCustomerSlots - numCustomerRegistrations,
        0
      )
    : Math.max(
        numOptionalSlots -
          maxMemberCount +
          Math.max(numRequiredCustomerSlots, numCustomerRegistrations),
        0
      );

  const numOptionalFlexSlots = supportsPublic
    ? 0 // When supportsPublic is true, all optional slots go to customers (no unknown customers needed)
    : Math.max(
        numOptionalSlots - numOptionalMemberSlots - numOptionalGuestSlots,
        0
      );

  return {
    numRequiredMemberSlots,
    numRequiredGuestSlots,
    numRequiredFlexSlots,
    numOptionalMemberSlots,
    numOptionalGuestSlots,
    numOptionalFlexSlots,
    numOptionalCustomerSlots,
    numRequiredCustomerSlots,
  };
};

export { getEventPrimaryChargePaymentData } from "./getEventPrimaryChargePaymentData";
